import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, FileText, ExternalLink, CheckCircle2, Pencil } from 'lucide-react';
import { PAGES, IMAGES } from '../mock';
import AccessGate from '../components/AccessGate';
import EditableImage from '../components/EditableImage';
import HtmlWithDataTables from '../components/DataTable';
import EditableText from '../components/EditableText';
import PageWidgets from '../components/PageWidgets';
import { useAuth } from '../context/AuthContext';

function EditPageIcon({ pageKey }) {
  const { isAdmin, customPages } = useAuth();
  const navigate = useNavigate();
  const hasRecord = (customPages || []).some((p) => p.page_key === pageKey);
  if (!isAdmin || !hasRecord) return null;
  return (
    <button
      type="button"
      data-testid={`edit-page-icon-${pageKey}`}
      title="Edit this page"
      onClick={() => navigate(`/admin/pages?edit=${pageKey}`)}
      className="inline-flex items-center gap-1 align-middle px-2 py-1 rounded text-xs font-title transition-opacity hover:opacity-90"
      style={{ background: 'var(--navy)', color: 'var(--cream)' }}
    >
      <Pencil size={13} /> Edit
    </button>
  );
}

function PageShell({ title, editKey, children }) {
  return (
    <div className="max-w-4xl mx-auto px-4 py-10 fade-in">
      <div className="flex items-center gap-3 flex-wrap mb-2">
        <h1 className="font-title text-3xl md:text-4xl" style={{ color: 'var(--maroon)' }}>
          {title}
        </h1>
        {editKey && <EditPageIcon pageKey={editKey} />}
      </div>
      <div className="h-1 w-20 mb-6" style={{ background: 'var(--gold)' }} />
      {children}
    </div>
  );
}

export default function ContentPage({ pageKey }) {
  const { customPages, isAdmin } = useAuth();
  const data = PAGES[pageKey];
  const dbPage = (customPages || []).find((p) => p.page_key === pageKey);
  const baseTitle = (dbPage && dbPage.title) || (data && data.title) || 'Page';

  if (dbPage && dbPage.draft && !(dbPage.publish_at && new Date(dbPage.publish_at).getTime() <= Date.now()) && !isAdmin) {
    return (
      <PageShell title="Coming soon">
        <p style={{ color: 'var(--olive)' }} data-testid="draft-notice">This page isn’t published yet. Please check back shortly.</p>
      </PageShell>
    );
  }

  if (!data) return <PageShell title="Page"><p>Content coming soon.</p></PageShell>;

  // A converted built-in page (builtin === false) renders as a normal editable
  // text page (title + intro + body paragraphs) instead of its rich schema.
  if (dbPage && dbPage.builtin === false) {
    const paras = (dbPage.body || '').split(/\n{2,}/).map((s) => s.trim()).filter(Boolean);
    return (
      <AccessGate pageKey={pageKey}>
        <PageShell editKey={pageKey} title={<EditableText k={`${pageKey}.title`} as="span" text={baseTitle} multiline={false} />}>
          {dbPage.html ? (
            <HtmlWithDataTables html={dbPage.html} />
          ) : (<>
          {dbPage.intro && (
            <EditableText k={`${pageKey}.intro`} as="p" text={dbPage.intro} className="block text-[15px] leading-relaxed mb-5 font-medium" />
          )}
          {paras.map((p, i) => (
            <EditableText key={i} k={`${pageKey}.cp${i}`} as="p" text={p} className="block text-[15px] leading-relaxed mb-4" />
          ))}
          {!dbPage.intro && paras.length === 0 && !(dbPage.widgets && dbPage.widgets.length) && (
            <p style={{ color: 'var(--olive)' }}>This page has no content yet.</p>
          )}
          </>)}
          <PageWidgets widgets={dbPage.widgets} />
        </PageShell>
      </AccessGate>
    );
  }

  return (
    <AccessGate pageKey={pageKey}>
      <PageShell editKey={pageKey} title={<EditableText k={`${pageKey}.title`} as="span" text={baseTitle} multiline={false} />}>
        {data.image && (
          <div className="float-none md:float-right md:ml-6 mb-5 w-full md:w-72">
            <EditableImage
              imgKey={`page-${pageKey}`}
              src={IMAGES[data.image]}
              alt={data.title}
              className="w-full h-48 object-cover rounded shadow-md"
              style={{ border: '4px solid white' }}
            />
          </div>
        )}

        {data.intro && (
          <EditableText
            as="p"
            k={`${pageKey}.intro`}
            text={data.intro}
            className="block text-[15px] leading-relaxed mb-5 font-medium"
          />
        )}

        {data.paragraphs &&
          data.paragraphs.map((p, i) => (
            <EditableText
              key={i}
              as="p"
              k={`${pageKey}.p${i}`}
              text={p}
              className="block text-[15px] leading-relaxed mb-4"
            />
          ))}

        {data.quote && (
          <EditableText
            as="blockquote"
            k={`${pageKey}.quote`}
            text={data.quote}
            multiline={false}
            className="block font-title text-2xl text-center my-8 py-4"
            style={{ color: 'var(--maroon)', borderTop: '1px solid var(--olive)', borderBottom: '1px solid var(--olive)' }}
          />
        )}

        {data.list && (
          <ul className="space-y-2 my-5">
            {data.list.map((li, i) => (
              <li key={i} className="flex items-start gap-2 text-[15px]">
                <CheckCircle2 size={18} className="mt-0.5 flex-shrink-0" style={{ color: 'var(--navy)' }} />
                <EditableText as="span" k={`${pageKey}.li${i}`} text={li} multiline={false} />
              </li>
            ))}
          </ul>
        )}

        {data.table && (
          <div className="overflow-x-auto my-6 clear-both">
            <table className="w-full text-left border-collapse bg-white rounded shadow-sm overflow-hidden">
              <thead>
                <tr style={{ background: 'var(--navy)' }}>
                  {data.table.head.map((h) => (
                    <th key={h} className="px-4 py-2.5 font-title text-sm" style={{ color: 'var(--cream)' }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.table.rows.map((row, i) => (
                  <tr key={i} style={{ background: i % 2 ? 'rgba(0,0,0,0.03)' : 'white' }}>
                    {row.map((cell, j) => (
                      <td key={j} className="px-4 py-2.5 text-sm border-t" style={{ borderColor: 'rgba(0,0,0,0.06)' }}>
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {data.docs && (
          <ul className="space-y-3 my-6 clear-both">
            {data.docs.map((d, i) => (
              <li
                key={i}
                className="flex items-center justify-between bg-white rounded p-3 shadow-sm border"
                style={{ borderColor: 'rgba(0,0,0,0.08)' }}
              >
                <span className="flex items-center gap-3">
                  <FileText size={20} style={{ color: 'var(--maroon)' }} />
                  <span className="text-sm">{d.name}</span>
                </span>
                <button
                  className="flex items-center gap-1 px-3 py-1.5 rounded text-sm font-title transition-colors"
                  style={{ background: 'var(--navy)', color: 'var(--cream)' }}
                  onClick={() => alert('Mocked download \u2014 will be wired to the backend.')}
                >
                  <Download size={14} /> {d.size}
                </button>
              </li>
            ))}
          </ul>
        )}

        {data.linkList && (
          <ul className="space-y-2 my-5">
            {data.linkList.map((l, i) => (
              <li key={i}>
                <a
                  href={l.url}
                  target={/^https?:/i.test(l.url) ? '_blank' : undefined}
                  rel={/^https?:/i.test(l.url) ? 'noopener noreferrer' : undefined}
                  data-testid={`useful-link-${i}`}
                  className="inline-flex items-center gap-2 text-[15px] hover:underline"
                  style={{ color: 'var(--navy)' }}
                >
                  <ExternalLink size={15} /> {l.name}
                </a>
              </li>
            ))}
          </ul>
        )}

        {data.cta && (
          <div className="clear-both mt-6">
            <button
              className="inline-flex items-center gap-2 px-5 py-3 rounded font-title transition-colors hover:opacity-90"
              style={{ background: 'var(--maroon)', color: 'var(--cream)' }}
              onClick={() => alert(data.cta.note)}
            >
              <Download size={18} /> {data.cta.label}
            </button>
          </div>
        )}
        {dbPage && <div className="clear-both"><PageWidgets widgets={dbPage.widgets} /></div>}
      </PageShell>
    </AccessGate>
  );
}
